import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import 'antd/dist/reset.css'; // Ant Design v6 styles
import App from './App';
import { BrowserRouter } from 'react-router-dom';

// Suppress ResizeObserver errors (common with react-big-calendar and other libraries)
// BUT keep all other console logs visible for debugging
const originalError = console.error;
const originalWarn = console.warn;
const originalLog = console.log;

// Keep console.log completely untouched - never suppress it
// console.log = originalLog; // Already default, no need to override

console.error = (...args) => {
  const errorMessage = args[0]?.toString() || '';
  if (
    errorMessage.includes('ResizeObserver loop') ||
    errorMessage.includes('ResizeObserver loop completed') ||
    errorMessage.includes('ResizeObserver loop limit exceeded')
  ) {
    return;
  }
  originalError.call(console, ...args);
};

console.warn = (...args) => {
  const warnMessage = args[0]?.toString() || '';
  if (
    warnMessage.includes('ResizeObserver loop') ||
    warnMessage.includes('ResizeObserver loop completed') ||
    warnMessage.includes('ResizeObserver loop limit exceeded')
  ) {
    return;
  }
  originalWarn.call(console, ...args);
};

// Enable DevTools shortcut (F12) - ensure it's not blocked
if (typeof window !== 'undefined') {
  // Add a global function to help debug
  window.debugAuth = () => {
    console.log('=== AUTH DEBUG INFO ===');
    console.log('Token:', localStorage.getItem('token') ? 'EXISTS' : 'MISSING');
    console.log('Token value:', localStorage.getItem('token')?.substring(0, 30) + '...');
    console.log('UserData:', localStorage.getItem('userData') ? 'EXISTS' : 'MISSING');
    console.log('Last Login Time:', localStorage.getItem('last_login_time'));
    console.log('Current Path:', window.location.pathname);
    console.log('=====================');
  };
  
  // Log on page load to help debug
  console.log('🔧 DevTools enabled. Press F12 to open. Type debugAuth() in console for auth info.');
}

// Suppress ResizeObserver errors in window error handler (before React catches it)
const originalErrorHandler = window.onerror;
window.onerror = (message, source, lineno, colno, error) => {
  if (
    typeof message === 'string' &&
    (message.includes('ResizeObserver loop') ||
     message.includes('ResizeObserver loop completed') ||
     message.includes('ResizeObserver loop limit exceeded'))
  ) {
    return true; // Suppress the error
  }
  if (originalErrorHandler) {
    return originalErrorHandler(message, source, lineno, colno, error);
  }
  return false;
};

// Also catch unhandled promise rejections
window.addEventListener('unhandledrejection', (e) => {
  if (
    e.reason?.message?.includes('ResizeObserver loop') ||
    e.reason?.toString()?.includes('ResizeObserver loop') ||
    e.reason?.message?.includes('ResizeObserver loop limit exceeded')
  ) {
    e.preventDefault();
  }
});

// Suppress React error overlay for ResizeObserver errors
if (process.env.NODE_ENV === 'development') {
  const originalConsoleError = window.console.error;
  window.console.error = (...args) => {
    const message = args[0]?.toString() || '';
    if (
      message.includes('ResizeObserver loop') ||
      message.includes('ResizeObserver loop completed') ||
      message.includes('ResizeObserver loop limit exceeded')
    ) {
      return;
    }
    originalConsoleError.apply(console, args);
  };
}

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    // Check if it's a ResizeObserver error
    const errorStr = error?.message || error?.toString() || '';
    if (
      errorStr.includes('ResizeObserver loop') ||
      errorStr.includes('ResizeObserver loop completed') ||
      errorStr.includes('ResizeObserver loop limit exceeded')
    ) {
      return null; // Don't update state for ResizeObserver errors
    }
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Suppress ResizeObserver errors
    const errorStr = error?.message || error?.toString() || '';
    if (
      errorStr.includes('ResizeObserver loop') ||
      errorStr.includes('ResizeObserver loop completed') ||
      errorStr.includes('ResizeObserver loop limit exceeded')
    ) {
      return;
    }
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <h1>Something went wrong.</h1>;
    }
    return this.props.children;
  }
}

// Additional global error handler for React DevTools overlay
if (typeof window !== 'undefined') {
  const handleError = (event) => {
    const message = event.message || event.error?.message || '';
    if (
      message.includes('ResizeObserver loop') ||
      message.includes('ResizeObserver loop completed') ||
      message.includes('ResizeObserver loop limit exceeded')
    ) {
      event.preventDefault();
      event.stopPropagation();
      return false;
    }
  };
  
  window.addEventListener('error', handleError, true);
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </ErrorBoundary>
  </React.StrictMode>
);

